import { botApi, type BacktestEventRaw } from '../api'
import type { OrderData } from '../components/GraphCandle'
import { toApiTimestamp } from '../utils/timeParsing'

const MAX_CACHE_ENTRIES = 50
const cache = new Map<string, OrderData[]>()

function cacheKey(btid: string, startTime: string, endTime: string): string {
  return `${btid}:${startTime}:${endTime}`
}

function cacheSet(key: string, value: OrderData[]): void {
  if (cache.has(key)) cache.delete(key)
  cache.set(key, value)
  while (cache.size > MAX_CACHE_ENTRIES) {
    const oldest = cache.keys().next().value
    if (oldest !== undefined) cache.delete(oldest)
    else break
  }
}

function toUnixSeconds(dateStr: string): number {
  const utc = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'
  return Math.floor(new Date(utc).getTime() / 1000)
}

function transformEvent(e: BacktestEventRaw): OrderData | null {
  if (!e.payload) return null
  const p = e.payload

  if (e.event_type === 'position_opened') {
    if (!p.entry_time || !p.type || p.entry_price == null) return null
    return {
      orderId: e.event_id,
      time: toUnixSeconds(p.entry_time),
      type: p.type as 'BUY' | 'SELL',
      price: p.entry_price,
      sl: p.sl ?? 0,
      tp: p.tp ?? 0,
      eventType: 'opened',
      volume: p.volume,
    }
  }

  if (e.event_type === 'position_closed') {
    if (!p.exit_time || !p.type || p.exit_price == null) return null
    return {
      orderId: e.event_id,
      time: toUnixSeconds(p.exit_time),
      type: p.type as 'BUY' | 'SELL',
      price: p.exit_price,
      sl: p.sl ?? 0,
      tp: p.tp ?? 0,
      eventType: 'closed',
      volume: p.volume,
      entryPrice: p.entry_price,
      exitPrice: p.exit_price,
      pnl: p.pnl,
      exitReason: p.exit_reason,
      balance: p.balance,
      commissionCost: p.commission_cost,
    }
  }

  return null
}

export async function fetchBacktestEvents(
  btid: string,
  startTs: number,
  endTs: number,
): Promise<OrderData[]> {
  const startTime = toApiTimestamp(startTs)
  const endTime = toApiTimestamp(endTs)
  const key = cacheKey(btid, startTime, endTime)

  const cached = cache.get(key)
  if (cached) return cached

  try {
    const events = await botApi.getBacktestEventsWindow(btid, startTime, endTime)
    const orders = events.map(transformEvent).filter((o): o is OrderData => o !== null)
    orders.sort((a, b) => a.time - b.time)
    cacheSet(key, orders)
    return orders
  } catch {
    return []
  }
}

export function clearEventCache(): void {
  cache.clear()
}
